/**
 * @fileoverview Why a place came up in the search box (2026-09-28, OQ-57 (4)):
 * the words of its description around what was searched for.
 *
 * The organization's search (`searchPlaces`) matches the name, the address
 * and the description. A place found by its name or address says why on its
 * own; one found only by its description — 「個室」, 「6人」 — would not, so
 * the result carries a short excerpt with the match in it. The excerpt is
 * made in the Worker from the row the search already read; the description
 * itself (up to 2,000 characters) never goes into the response.
 *
 * **Matching is the database's**: `instr(lower(column), lower(text))`, where
 * SQLite's `lower()` folds ASCII only. `containsLike()` does the same, so a
 * place the SQL found is one this finds, and the other way round — full-width
 * and half-width, hiragana and katakana stay different characters in both.
 */

/** Characters of context before the match, and after it. */
export const EXCERPT_BEFORE = 20;
export const EXCERPT_AFTER = 40;

/** A description around its match, cut to a few words either side. */
export interface SearchExcerpt {
  /** The words before the match; `…` leads when there was more before them. */
  before: string;
  /** The matched text, as the description has it. */
  match: string;
  after: string;
  /** Text was cut before `before` / after `after`. */
  truncatedStart: boolean;
  truncatedEnd: boolean;
}

/** SQLite's `lower()`: A–Z only. */
function asciiLower(value: string): string {
  return value.replace(/[A-Z]/g, (letter) => letter.toLowerCase());
}

/** Whether `haystack` contains `needle` the way the search's SQL decides it. */
export function containsLike(haystack: string | null, needle: string): boolean {
  if (haystack === null || needle.length === 0) return false;
  return asciiLower(haystack).includes(asciiLower(needle));
}

/** Runs of white space, line breaks included, read as one space in a one-line excerpt. */
function oneLine(value: string): string {
  return value.replace(/\s+/g, ' ');
}

/**
 * The excerpt for a search result, or `null` when there is nothing to
 * explain: the name or the address already contains `text`, or the
 * description does not.
 */
export function searchExcerpt(
  place: { name: string; address: string | null; description: string | null },
  text: string,
): SearchExcerpt | null {
  const query = text.trim();
  if (query.length === 0 || place.description === null) return null;
  if (containsLike(place.name, query) || containsLike(place.address, query)) return null;

  const at = asciiLower(place.description).indexOf(asciiLower(query));
  if (at < 0) return null;

  // Counted in characters (code points), as a reader counts them: a window
  // cut in UTF-16 units could split an emoji in half.
  const head = Array.from(place.description.slice(0, at));
  const match = place.description.slice(at, at + query.length);
  const tail = Array.from(place.description.slice(at + query.length));
  return {
    before: oneLine(head.slice(-EXCERPT_BEFORE).join('')),
    match: oneLine(match),
    after: oneLine(tail.slice(0, EXCERPT_AFTER).join('')),
    truncatedStart: head.length > EXCERPT_BEFORE,
    truncatedEnd: tail.length > EXCERPT_AFTER,
  };
}
